import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Check, ExternalLink, ShieldAlert, Tv, Radio } from "lucide-react";
import { useMyCinema } from "../context/MyCinemaContext";
import { useTranslate } from "../context/TranslateContext";

const STATUS_COLORS = {
  Running: "text-emerald2 border-emerald2/40 bg-emerald2/10",
  Concluded: "text-ash border-edge bg-black/[0.04]",
  Upcoming: "text-gold border-gold/40 bg-gold/10",
  Paused: "text-saffron border-saffron/40 bg-saffron/10",
  Cancelled: "text-crimson border-crimson/40 bg-crimson/10",
};

export const StatusPill = ({ status, testId }) => {
  const { t } = useTranslate();
  return <span data-testid={testId} className={`rounded-full border px-3 py-1 font-body text-xs ${STATUS_COLORS[status] || STATUS_COLORS.Concluded}`}>{t(status)}</span>;
};

export const FollowButton = ({ id, accent = "#D4AF37" }) => {
  const { inList, toggle } = useMyCinema();
  const { t } = useTranslate();
  const following = inList("watchlist", id);
  return (
    <button onClick={() => toggle("watchlist", id)} data-testid={`follow-${id}`}
      className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-body text-sm transition-colors duration-300"
      style={following ? { borderColor: accent, background: accent + "22", color: accent } : { borderColor: "#C9B48C", color: "#2A1D12" }}>
      {following ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      {t(following ? "Following" : "Follow")}
    </button>
  );
};

export const WorkCard = ({ s, i = 0 }) => {
  const { t } = useTranslate();
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.04 }}>
      <Link to={`/series/${s.slug}`} data-testid={`work-card-${s.slug}`}>
        <motion.div whileHover={{ y: -6 }} className="group overflow-hidden rounded-2xl border border-edge bg-surface/40">
          <div className="relative aspect-[2/3] overflow-hidden">
            <img src={s.poster} alt={s.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink to-transparent" />
            <div className="absolute left-3 top-3 rounded-full px-2.5 py-1 font-body text-[0.65rem]" style={{ background: s.accent + "cc", color: "#0F0B0D" }}>{s.language}</div>
            <div className="absolute bottom-3 left-3 right-3">
              <div className="font-display text-xl text-paper drop-shadow-lg">{s.title}</div>
              <div className="font-body text-xs text-paper/70">{t(s.category)}</div>
            </div>
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
};

export const AvailabilityRow = ({ a, testId }) => {
  const { t } = useTranslate();
  return (
    <div data-testid={testId} className="flex flex-wrap items-center gap-3 rounded-xl border border-edge bg-surface/50 px-4 py-3">
      <span className="font-body font-medium text-cream">{a.provider}</span>
      <span className="rounded-full bg-black/[0.04] px-2.5 py-0.5 font-body text-xs text-ash">{t(a.access)}</span>
      {a.note && <span className="font-body text-xs text-ash">{t(a.note)}</span>}
      <span className="ml-auto flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full border border-saffron/30 bg-saffron/10 px-2 py-0.5 font-body text-[0.65rem] text-saffron" title="No licensed feed connected">
          <ShieldAlert className="h-3 w-3" /> {t("Not verified")} · {a.checked}
        </span>
        <a href={a.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-body text-xs text-gold hover:underline">
          {t("Check availability")} <ExternalLink className="h-3 w-3" />
        </a>
      </span>
    </div>
  );
};

export const SectionHead = ({ overline, title, accent, icon: Icon, right }) => {
  const { t } = useTranslate();
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        {overline && <span className="text-xs uppercase tracking-[0.28em]" style={{ color: accent || "#D4AF37" }}>{t(overline)}</span>}
        <h2 className="mt-3 font-display text-4xl font-semibold leading-tight tracking-tight text-cream md:text-5xl">
          {Icon && <Icon className="mb-1 mr-3 inline h-8 w-8" style={{ color: accent || "#D4AF37" }} />}{title}
        </h2>
      </div>
      {right}
    </div>
  );
};
